import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { Roles } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { User } from '../account-management.types';

interface Props {
  open: boolean;
  user: User | null;
  deleteData: boolean;
  tenantUsers: OptionType[];
  toMoveUser: string | undefined;
  loading?: boolean;

  userRole: Roles;

  handleDeleteDataChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleToMoveUserChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleConfirm(): void;

  handleClose(): void;
}

const DeleteUserDialog: React.FC<Props> = ({
  open,
  user,
  deleteData,
  tenantUsers,
  toMoveUser,
  loading,
  userRole,
  handleDeleteDataChange,
  handleToMoveUserChange,
  handleConfirm,
  handleClose,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const userName = `${user?.firstname ?? ''} ${user?.lastname ?? ''}`;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('deleteUser')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Typography className={classes.text}>
            <Trans i18nKey={'deleteUserWarning1'} values={{ userName }}>
              Should the user <strong>username</strong> really be deleted?
            </Trans>
          </Typography>
          <Typography className={cx(classes.text, 'highlight')}>{t('deleteUserWarning2')}</Typography>
          {userRole === Roles.ADMIN || userRole === Roles.AGENCY ? (
            <>
              <Stack className={classes.box} gap={1}>
                <Typography className={classes.text}>
                  <Trans i18nKey={'dataProceedQuestion'} values={{ userName }}>
                    What should happen to the data of <strong>username</strong>?
                  </Trans>
                </Typography>
                <Stack direction={'row'} gap={1} justifyContent={'center'} alignItems={'center'}>
                  <Typography className={cx(classes.secondaryText, deleteData ? 'active' : undefined)}>{t('delete')}</Typography>
                  <PaiSwitch checked={!deleteData} onChange={handleDeleteDataChange} />
                  <Typography className={cx(classes.secondaryText, !deleteData ? 'active' : undefined)}>{t('transfer')}</Typography>
                </Stack>
              </Stack>
              {deleteData ? (
                <Typography className={cx(classes.text, 'highlight')}>{t('dataProceedInfo')}</Typography>
              ) : (
                <>
                  <Typography className={cx(classes.text, 'highlight')}>{t('dataTransferQuestion')}</Typography>
                  {!_.isNil(toMoveUser) ? (
                    <PaiSelect value={toMoveUser} onChange={handleToMoveUserChange}>
                      {tenantUsers.map((user) => (
                        <PaiMenuItem key={user.value} value={user.value}>
                          {user.label}
                        </PaiMenuItem>
                      ))}
                    </PaiSelect>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('ok')}
          size={'small'}
          border={'rounded'}
          action={handleConfirm}
          disabled={loading || (!deleteData && toMoveUser === '')}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  box: {
    padding: '10px 16px',
    borderRadius: '8px',
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.warning': {
      color: theme.palette.warning.main,
      fontWeight: 'bold',
    },
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.secondary,
    '&.active': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default DeleteUserDialog;
